import { z } from 'zod';
import { API_BASE, apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * HR+ (kadry) — ustawienia połączenia, sprawdzenie i podgląd dopasowania (etap 1, 06.10.2026).
 *
 * ⚠️ SCHEMATY ŁAGODNE (`passthrough`, `nullish`): ekran ma pokazać to, co serwer oddał, a nie
 * zamienić udany odczyt w błąd, bo serwer dołożył pole albo zostawił puste.
 */

export const KOLEJNOSCI_NAZWY = ['IMIE_NAZWISKO', 'NAZWISKO_IMIE'] as const;
export type KolejnoscNazwy = (typeof KOLEJNOSCI_NAZWY)[number];

const UstawieniaHrSchema = z
  .object({
    skonfigurowana: z.boolean(),
    adres: z.string().nullish(),
    serwisId: z.string().nullish(),
    kluczUstawiony: z.boolean(),
    kluczPoczatek: z.string().nullish(),
    kluczNieczytelny: z.boolean().default(false),
    zrodloKarty: z.string(),
    kolejnoscNazwy: z.enum(KOLEJNOSCI_NAZWY),
    wlaczona: z.boolean(),
    sprawdzonoO: z.string().nullish(),
    ostatniBlad: z.string().nullish(),
    sejf: z.boolean().default(true),
    zakladajMpk: z.boolean().default(true),
    progBlokad: z.number().default(100),
    progProcent: z.number().default(20),
    /** „Blokuj osoby spoza HR+" i grupy z wyjątkiem (12.10.2026); `default` — starsza instancja ich nie odda. */
    blokujSpoza: z.boolean().default(true),
    wyjatkiGrup: z.array(z.string()).default([]),
    wlaczonaO: z.string().nullish(),
    ostatniPrzebiegO: z.string().nullish(),
    ostatniPrzebieg: z.string().nullish(),
    wstrzymanaO: z.string().nullish(),
    wstrzymanaBlokad: z.number().nullish(),
    /**
     * BEZPIECZNIK (09.10.2026): powód, podstawa procentu i kogo paczka odcina. `nullish`/`default`, bo
     * starsza instancja tych pól nie odda — schemat ostrzejszy od odpowiedzi zamienia odczyt w błąd.
     */
    wstrzymanaPowod: z.string().nullish(),
    wstrzymanaZ: z.number().nullish(),
    wstrzymanaPelna: z.boolean().default(false),
    wstrzymanaKto: z
      .array(z.object({ numer: z.string(), nazwa: z.string(), jak: z.string() }).passthrough())
      .nullish()
      .transform((v) => v ?? []),
    uzgodnienieO: z.string().nullish(),
    /** Znaczniki na wyspie (12.10.2026) — `nullish`, bo starsza instancja tego nie odda. */
    liczby: z
      .object({
        zHr: z.number(),
        zHrZablokowani: z.number(),
        spoza: z.number(),
        spozaAktywni: z.number(),
        wyjatki: z.number().default(0),
        spozaZablokowani: z.number().default(0),
      })
      .nullish(),
  })
  .passthrough();
export type UstawieniaHr = z.infer<typeof UstawieniaHrSchema>;

const SprawdzenieSchema = z
  .object({
    total: z.number(),
    cursor: z.string(),
    pola: z.array(z.string()).default([]),
    atrybuty: z.array(z.string()).default([]),
    przyklad: z
      .object({ nazwa: z.string(), numerKarty: z.string().nullish(), bladKarty: z.string().nullish() })
      .passthrough()
      .nullish(),
    /** Czy rekord niesie zakładkę Vending (blok `vending`); `nullish` — starsza instancja tego nie odda. */
    zBlokiem: z.boolean().nullish(),
  })
  .passthrough();
export type SprawdzenieHr = z.infer<typeof SprawdzenieSchema>;

export const KATEGORIE_PODGLADU = ['zmiany', 'nowi', 'przywroceni', 'bezAutomatu', 'bezKarty', 'zlyNumer', 'zdublowani', 'tylkoUNas'] as const;
export type KategoriaPodgladu = (typeof KATEGORIE_PODGLADU)[number];

const PozycjaSchema = z
  .object({
    hrId: z.string().nullish(),
    numer: z.string().nullish(),
    nazwa: z.string(),
    zmiany: z.array(z.string()).nullish(),
    powod: z.string().nullish(),
  })
  .passthrough();
export type PozycjaPodgladu = z.infer<typeof PozycjaSchema>;

const PodgladSchema = z
  .object({
    total: z.number(),
    wHr: z.number(),
    dopasowani: z.number(),
    bezZmian: z.number(),
    zmianyNazwy: z.number(),
    zmianyMpk: z.number(),
    doOdblokowania: z.number(),
    doZablokowania: z.number().default(0),
    nowi: z.number(),
    przywroceni: z.number(),
    /** Z kartą, ale bez dostępu do automatów — do Vendi nie trafią (12.10.2026); `default`, bo starsza instancja tego nie odda. */
    bezAutomatu: z.number().default(0),
    bezKarty: z.number(),
    zlyNumer: z.number(),
    zdublowani: z.number(),
    tylkoUNas: z.number(),
    /** Spoza HR+ przy przełączniku (12.10.2026): do blokady, z wyjątkiem, do odblokowania. */
    spozaDoBlokady: z.number().default(0),
    spozaWyjatki: z.number().default(0),
    spozaDoOdblokowania: z.number().default(0),
    dlugieNazwy: z.number().default(0),
    zaDlugieMpk: z.number().default(0),
    /**
     * ZAKŁADKA VENDING (etap 3, 08.10.2026). `default(0)`, bo instancja starsza od tej zmiany tych pól
     * nie odda — a schemat ostrzejszy od odpowiedzi zamienia udany odczyt w błąd.
     */
    zmianyAliasu: z.number().default(0),
    zmianyFlag: z.number().default(0),
    zmianyGrup: z.number().default(0),
    zmianyAutomatow: z.number().default(0),
    obceIdentyfikatory: z.number().default(0),
    zBlokiem: z.number().default(0),
    bezBloku: z.number().default(0),
    zlyBlok: z.number().default(0),
    noweMpk: z.array(z.string()).default([]),
    noweMpkIle: z.number().default(0),
    probki: z.record(z.string(), z.array(PozycjaSchema)).default({}),
  })
  .passthrough();
export type PodgladHr = z.infer<typeof PodgladSchema>;

export const fetchUstawieniaHr = (groupId: string) =>
  apiGet(`/hr-plus/ustawienia?groupId=${encodeURIComponent(groupId)}`, UstawieniaHrSchema);

export const zapiszUstawieniaHr = (dto: {
  groupId: string;
  adres: string;
  serwisId: string;
  klucz?: string;
  zrodloKarty: string;
  kolejnoscNazwy: KolejnoscNazwy;
  zakladajMpk: boolean;
  progBlokad: number;
  blokujSpoza: boolean;
  wyjatkiGrup: string[];
}) => apiSend('PUT', '/hr-plus/ustawienia', dto, UstawieniaHrSchema);

/** Wyjątek osoby od „Blokuj osoby spoza HR+" — przełącznik na karcie pracownika (12.10.2026). */
export const ustawWyjatekHr = (groupId: string, employeeId: string, wyjatek: boolean) =>
  apiSend('PUT', '/hr-plus/wyjatek', { groupId, employeeId, wyjatek }, z.object({ wyjatek: z.boolean() }));

/** Etap 2 — od włączenia HR+ prowadzi pracowników wyspy; pierwszy przebieg (pełna lista) idzie w tle. */
export const wlaczHr = (groupId: string) => apiPost('/hr-plus/wlacz', { groupId }, UstawieniaHrSchema);
export const wylaczHr = (groupId: string) => apiPost('/hr-plus/wylacz', { groupId }, UstawieniaHrSchema);
/** Przepuszczenie paczki zatrzymanej przez bezpiecznik — jednorazowo. */
export const przepuscHr = (groupId: string) => apiPost('/hr-plus/przepusc', { groupId }, UstawieniaHrSchema);
export const synchronizujHr = (groupId: string) => apiPost('/hr-plus/synchronizuj', { groupId }, UstawieniaHrSchema);

/** Plik startowy dla HR+ (CSV) — zwykły odnośnik z ciasteczkiem sesji, jak kopia portalu. */
export const adresEksportuHr = (groupId: string) => `${API_BASE}/hr-plus/eksport?groupId=${encodeURIComponent(groupId)}`;

export const sprawdzHr = (groupId: string) => apiPost('/hr-plus/sprawdz', { groupId }, SprawdzenieSchema);

export const podgladHr = (groupId: string) => apiPost('/hr-plus/podglad', { groupId }, PodgladSchema);

/** Nazwy MPK z HR+ (kanoniczny kod → nazwa) — podpowiedź przy dodawaniu kodów z kart do słownika. */
export const nazwyMpkHr = (groupId: string) =>
  apiPost('/hr-plus/nazwy-mpk', { groupId }, z.object({ nazwy: z.record(z.string(), z.string()) }));

/**
 * ŹRÓDŁO NUMERU KARTY w formularzu: lista („numer ewidencyjny" / „pole dodatkowe") plus nazwa pola.
 * Serwer trzyma jedną ścieżkę (`employeeNo` albo `attributes.<nazwa>`) — tu ją rozkładamy i składamy.
 */
export const EMPLOYEE_NO = 'employeeNo';
const PREFIKS_ATRYBUTU = 'attributes.';

export function rozlozZrodlo(sciezka: string): { rodzaj: 'employeeNo' | 'atrybut' | 'inne'; atrybut: string } {
  if (sciezka === EMPLOYEE_NO) return { rodzaj: 'employeeNo', atrybut: '' };
  if (sciezka.startsWith(PREFIKS_ATRYBUTU)) return { rodzaj: 'atrybut', atrybut: sciezka.slice(PREFIKS_ATRYBUTU.length) };
  return { rodzaj: 'inne', atrybut: sciezka };
}

export function zlozZrodlo(rodzaj: 'employeeNo' | 'atrybut' | 'inne', atrybut: string): string {
  if (rodzaj === 'employeeNo') return EMPLOYEE_NO;
  if (rodzaj === 'atrybut') return `${PREFIKS_ATRYBUTU}${atrybut.trim()}`;
  return atrybut.trim();
}

/**
 * ADRES FEEDU Z HR+ (`https://hr…/api/feed/<serwis>/employees?…` albo `…/changes?…`) wklejony w pole
 * adresu albo serwisu wypełnia oba naraz (13.10.2026). `null` = to nie adres feedu, pole bierze tekst
 * jak jest. Serwer i tak rozbiera adres w `serwisId` sam (`idSerwisu`) — tu chodzi o to, żeby człowiek
 * od razu widział, co zostanie zapisane.
 */
export function rozbierzAdresFeedu(tekst: string): { adres: string; serwisId: string } | null {
  const t = tekst.trim();
  if (!URL.canParse(t)) return null;
  const u = new URL(t);
  const m = /^\/api\/feed\/([^/]+)/.exec(u.pathname);
  return m ? { adres: u.origin, serwisId: decodeURIComponent(m[1]) } : null;
}
